import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Search,
  ChevronDown,
  User,
  Wrench,
  Play,
  Rocket,
  Lock,
  Mail,
  HelpCircle,
  CreditCard,
  Film,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/contexts/LanguageContext";
import { traducoesTopico } from "@/i18n/topico";

const BRAND_PRIMARY = "#4b36cc";

// Explorar tópicos: categorias com subcategorias e itens
const EXPLORAR_TOPICOS = [
  {
    id: "conta-faturamento",
    title: "Conta e faturamento",
    icon: User,
    subTopics: [
      {
        title: "Configurações da conta",
        items: [
          { label: "Como mudar de plano", id: "mudar-plano" },
          { label: "Como cancelar", id: "cancelar" },
          { label: "Como alterar ou redefinir a senha", id: "redefinir-senha" },
          { label: "Como atualizar informações da conta", id: "atualizar-conta" },
          { label: "Como compartilhar sua conta", id: "compartilhar-conta" },
          { label: "Como manter sua conta segura", id: "conta-segura" },
        ],
      },
      {
        title: "Como pagar",
        items: [
          { label: "Formas de pagamento", id: "formas-pagamento" },
          { label: "Atualizar cartão", id: "atualizar-cartao" },
        ],
      },
    ],
  },
  {
    id: "corrigir-problema",
    title: "Corrigir um problema",
    icon: Wrench,
    subTopics: [
      { title: "Problemas na conta", items: [{ label: "Acesso negado", id: "acesso-negado" }] },
      { title: "Problemas de faturamento", items: [{ label: "Cobrança indevida", id: "cobranca" }] },
      { title: "Códigos de erro", items: [{ label: "Erros comuns", id: "codigos-erro" }] },
      { title: "Problemas durante a reprodução", items: [{ label: "Vídeo não carrega", id: "video-nao-carrega" }] },
    ],
  },
  {
    id: "reproduzir-assistir",
    title: "Reproduzir e assistir",
    icon: Play,
    subTopics: [
      { title: "Perfil", items: [{ label: "Criar e editar perfil", id: "perfil" }] },
      { title: "Recursos e configurações", items: [{ label: "Qualidade de vídeo", id: "qualidade" }] },
      { title: "Séries e filmes", items: [{ label: "Como encontrar conteúdo", id: "conteudo" }] },
      { title: "Controle parental", items: [{ label: "Configurar restrições", id: "parental" }] },
      { title: "Jogos", items: [{ label: "Jogos na plataforma", id: "jogos" }] },
    ],
  },
  {
    id: "introducao",
    title: "Introdução",
    icon: Rocket,
    subTopics: [
      { title: "Como assinar", items: [{ label: "Passo a passo para assinar", id: "assinar" }] },
      { title: "Configuração do aparelho", items: [{ label: "Instalar no dispositivo", id: "config-aparelho" }] },
    ],
  },
];

const ATALHOS_ICONS = [Lock, Mail, HelpCircle, CreditCard, Film];

export default function Topico() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const t = traducoesTopico[lang];
  const [busca, setBusca] = useState("");

  const handleBusca = () => {
    if (busca.trim()) navigate(`/ajuda?q=${encodeURIComponent(busca)}`);
  };

  // Valor inicial do accordion: abrir a categoria que contém o id do tópico (se houver)
  const defaultAccordion = (() => {
    if (!id) return undefined;
    for (let i = 0; i < EXPLORAR_TOPICOS.length; i++) {
      const cat = EXPLORAR_TOPICOS[i];
      const hasId = cat.subTopics.some((st) =>
        st.items.some((item) => item.id === id || cat.id === id)
      );
      if (hasId) return cat.id;
    }
    return EXPLORAR_TOPICOS[0]?.id;
  })();

  return (
    <div
      className="min-h-screen flex flex-col bg-white text-[#3c3939] antialiased"
      style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}
    >
      {/* Idioma: mesma barra PT/EN/ES */}
      <header className="bg-white sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-end">
          <div className="flex items-center gap-1 text-sm text-gray-600">
            {(["pt", "en", "es"] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={`px-2 py-1 rounded transition-colors ${lang === l ? "text-[#3c3939] font-semibold" : "hover:text-[#3c3939]"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>
      {/* Título + busca */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-center text-[#3c3939] mb-6">
            {t.heroTitle}
          </h1>
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5"
              style={{ color: BRAND_PRIMARY }}
              aria-hidden
            />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleBusca()}
              placeholder={t.searchPlaceholder}
              className="w-full pl-12 pr-4 h-12 rounded-lg border border-gray-300 bg-white text-[#3c3939] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-base"
              aria-label={t.searchAriaLabel}
            />
          </div>
          <p className="text-sm text-gray-600 mt-3">
            {t.principaisAssuntos}{" "}
            {t.principaisLabels.map((a, i) => (
              <span key={a.href}>
                <Link to={a.href} className="hover:underline" style={{ color: BRAND_PRIMARY }}>
                  {a.label}
                </Link>
                {i < t.principaisLabels.length - 1 ? ", " : ""}
              </span>
            ))}
          </p>
        </div>
      </section>

      {/* Explorar tópicos — accordion */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8">
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-lg font-semibold text-[#3c3939] mb-1">{t.explorarTopicos}</h2>
          <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden />
        </div>

        <Accordion
          type="single"
          collapsible
          defaultValue={defaultAccordion}
          className="w-full space-y-0 rounded-lg border border-gray-200 bg-white overflow-hidden"
        >
          {EXPLORAR_TOPICOS.map((categoria, idx) => {
            const Icon = categoria.icon;
            const categoryTitle = t.categorias[idx]?.title ?? categoria.title;
            return (
              <AccordionItem key={categoria.id} value={categoria.id} className="border-b border-gray-200 last:border-b-0">
                <AccordionTrigger className="px-4 sm:px-6 py-4 hover:no-underline hover:bg-gray-50 [&[data-state=open]]:bg-gray-50">
                  <span className="flex items-center gap-3 text-left">
                    <Icon className="h-5 w-5 text-gray-500 shrink-0" />
                    <span className="font-medium text-[#3c3939]">{categoryTitle}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 sm:px-6 pb-4 pt-0">
                  <div className="space-y-6">
                    {categoria.subTopics.map((sub) => (
                      <div key={sub.title}>
                        <p className="font-medium text-[#3c3939] mb-2">{sub.title}</p>
                        <ul className="space-y-1.5">
                          {sub.items.map((item) => (
                            <li key={item.id}>
                              <Link
                                to={`/artigo/${item.id}`}
                                className="flex items-center gap-2 text-gray-700 text-sm no-underline hover:opacity-80"
                style={{ color: BRAND_PRIMARY }}
                              >
                                <span className="inline-block w-4 h-4 border border-gray-300 rounded-sm shrink-0" aria-hidden />
                                {item.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* Atalhos */}
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-[#3c3939] mb-4">{t.atalhos}</h2>
          <ul className="space-y-2">
            {t.atalhosLabels.map((atalho, i) => {
              const Icon = ATALHOS_ICONS[i];
              return (
                <li key={atalho.href}>
                  <Link
                    to={atalho.href}
                    className="flex items-center gap-3 py-2 text-gray-700 no-underline rounded-lg hover:bg-gray-50 px-2 -mx-2 hover:opacity-80"
                    style={{ color: BRAND_PRIMARY }}
                  >
                    <Icon className="h-5 w-5 text-gray-500 shrink-0" />
                    <span>{atalho.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        {/* CTA */}
        <section className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-lg font-bold text-[#3c3939] mb-4">{t.precisaMaisAjuda}</p>
          <Link
            to="/suporte"
            className="inline-flex items-center justify-center h-12 px-6 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800 no-underline text-sm"
          >
            {t.entreContato}
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-auto py-8 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t.idioma}</span>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              {(["pt", "en", "es"] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLang(l)}
                  className={`px-2 py-1 rounded transition-colors ${lang === l ? "text-[#3c3939] font-semibold" : "hover:text-[#3c3939]"}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <nav className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
            <Link to="#" className="hover:text-[#3c3939] no-underline">
              {t.termosUso}
            </Link>
            <Link to="#" className="hover:text-[#3c3939] no-underline">
              {t.privacidade}
            </Link>
            <Link to="#" className="hover:text-[#3c3939] no-underline">
              {t.preferenciasCookies}
            </Link>
            <Link to="#" className="hover:text-[#3c3939] no-underline">
              {t.informacoesCorporativas}
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
